/**
 * Should this keydown trigger a break-code paste in the dialog hosted at `host`?
 *
 * Bound per-dialog on `document:keydown` rather than via the hotkeys registry:
 * hotkeys-js' default filter ignores events raised inside form fields, and paste is
 * pressed with focus in a dialog. `event.code` (not `event.key`) keeps it working on
 * layouts where alt+v emits a character.
 *
 * Every open dialog sees every keydown, so exactly one must claim it:
 * 1. Host has no ngb-modal-window ancestor (only outside NgbModal) — fail open.
 * 2. Target inside SOME modal window — only that window's dialog fires. Disambiguates
 *    two open dialogs, and covers focus landing on the modal window itself (which is
 *    what happens after clicking or dragging the dialog title).
 * 3. Target inside NO modal window — the topmost PASTE-CAPABLE window fires. These
 *    dialogs are deliberately click-through, so the flagship flow is: open a dialog,
 *    click a grid row BEHIND it, alt+c, then alt+v with focus still on that cell.
 *    Requiring strict containment made that a silent no-op right after a
 *    successful-looking copy. Unrelated modals (filter, create-issue, breaks
 *    validation) can sit on top, so the search skips windows with no paste button —
 *    taking the last window outright handed the event to a dialog that ignores it.
 */
export function isPasteBreakCodesEvent(
  event: KeyboardEvent,
  host: HTMLElement,
): boolean {
  if (
    !event.altKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.metaKey ||
    event.code !== 'KeyV'
  ) {
    return false
  }

  const modalWindow = host.closest('ngb-modal-window')
  if (!modalWindow) return true

  // Guard the instanceof: a hand-rolled (never dispatched) event has a null target,
  // and `document` as a target has no `closest`.
  const target = event.target instanceof Element ? event.target : null
  const targetWindow = target?.closest('ngb-modal-window')
  if (targetWindow) return targetWindow === modalWindow

  // Filtered in JS rather than with `:has()`, which this app's browser targets predate.
  const pasteCapable = Array.from(
    host.ownerDocument.querySelectorAll('ngb-modal-window'),
  ).filter(w => w.querySelector('[data-cy="paste-break-codes"]') != null)
  return pasteCapable[pasteCapable.length - 1] === modalWindow
}
